import React, { useState, useEffect, useRef, useCallback } from 'react';

// Constants for better maintainability
const SEARCH_DEFAULTS = {
  PLACEHOLDER: "Search leads...",
  DEBOUNCE_MS: 300,
  MIN_WIDTH: 600,
  MAX_WIDTH: 1000,
} as const;

const SEARCH_STYLES = {
  BACKGROUND: 'rgba(255, 255, 255, 0.15)',
  BACKDROP_BLUR: 'blur(12px)',
  BORDER_FOCUSED: 'rgba(255, 255, 255, 0.4)',
  BORDER_DEFAULT: 'rgba(255, 255, 255, 0.2)',
  TEXT_COLOR: 'white',
  ICON_COLOR_FOCUSED: 'rgba(255, 255, 255, 1)',
  ICON_COLOR_DEFAULT: 'rgba(255, 255, 255, 0.8)',
} as const;

interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = SEARCH_DEFAULTS.PLACEHOLDER,
  value = "",
  onChange,
  onClear,
  debounceMs = SEARCH_DEFAULTS.DEBOUNCE_MS,
  disabled = false,
  className = "",
  'aria-label': ariaLabel = "Search leads"
}) => {
  // State management
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  
  // Refs
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Cleanup function for debounce timer
  const cleanupDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  // Debounced onChange effect
  useEffect(() => {
    cleanupDebounce();

    debounceRef.current = setTimeout(() => {
      onChange(localValue);
    }, debounceMs);

    return cleanupDebounce;
  }, [localValue, debounceMs, onChange, cleanupDebounce]);

  // Event handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalValue("");
    onChange("");
    onClear?.();
    inputRef.current?.focus();
  }, [onChange, onClear]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
    }
  }, [handleClear]);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  // Memoized styles
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: SEARCH_STYLES.BACKGROUND,
    backdropFilter: SEARCH_STYLES.BACKDROP_BLUR,
    border: `2px solid ${isFocused ? SEARCH_STYLES.BORDER_FOCUSED : SEARCH_STYLES.BORDER_DEFAULT}`,
    borderRadius: '12px',
    padding: '10px 16px',
    transition: 'all 0.3s ease',
    boxShadow: isFocused 
      ? '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
      : '0 4px 16px rgba(0, 0, 0, 0.05)',
    minWidth: `${SEARCH_DEFAULTS.MIN_WIDTH}px`,
    maxWidth: `${SEARCH_DEFAULTS.MAX_WIDTH}px`
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '16px',
    fontWeight: '500',
    color: SEARCH_STYLES.TEXT_COLOR
  };

  // Icon components for better maintainability
  const SearchIcon: React.FC = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      style={{ 
        marginRight: '12px',
        color: isFocused ? SEARCH_STYLES.ICON_COLOR_FOCUSED : SEARCH_STYLES.ICON_COLOR_DEFAULT,
        transition: 'color 0.2s ease'
      }}
      aria-hidden="true"
    >
      <circle 
        cx="11" 
        cy="11" 
        r="8" 
        stroke="currentColor" 
        strokeWidth="2"
      />
      <path 
        d="m21 21-4.35-4.35" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  const ClearIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path 
        d="M18 6L6 18M6 6l12 12" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  const clearButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '8px',
    padding: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  const hintStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    marginTop: '4px',
    padding: '4px 8px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(12px)',
    borderRadius: '6px',
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    zIndex: 10
  };

  return (
    <div className={`search-bar ${className}`} role="search">
      <div style={containerStyle}>
        <SearchIcon />

        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          style={inputStyle}
          aria-label={ariaLabel}
          autoComplete="off"
          spellCheck="false"
        />

        {localValue && (
          <button
            onClick={handleClear}
            type="button"
            style={clearButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
            }}
            aria-label="Clear search"
            tabIndex={0}
          >
            <ClearIcon />
          </button>
        )}
      </div>

      {localValue && (
        <div style={hintStyle} role="status" aria-live="polite">
          Press Esc to clear
        </div>
      )}
    </div>
  );
};